'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box } from '@terra/ui-styled-system';
import {
  Checkbox,
  computeGroupCheckboxState,
  GROUP_CHECKBOX_STATE,
  GroupToggleButton,
  type GroupCheckboxState,
} from '@terra/ui-design-system';
import type { AssetsTableRow } from './assets-table.model';
import { isAssetsTableGroupHeaderRow } from './assets-table.model';
import { useAssetsTableContext } from './AssetsTableContext';
import { rowClickBlockerProps } from '../rowClickBlocker';

const toCheckboxChecked = (
  state: GroupCheckboxState,
): boolean | typeof GROUP_CHECKBOX_STATE.INDETERMINATE =>
  state === GROUP_CHECKBOX_STATE.CHECKED
    ? true
    : state === GROUP_CHECKBOX_STATE.INDETERMINATE
      ? GROUP_CHECKBOX_STATE.INDETERMINATE
      : false;

function SelectionCheckboxFrame({ children }: { children: ReactNode }) {
  return (
    <Box
      w="full"
      h="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      {...rowClickBlockerProps}
    >
      {children}
    </Box>
  );
}

export function AssetsTableSelectAllHeader() {
  const t = useTranslations('proxy.assets.table');
  const { selectedAssetIds, loadedAssetIds, onSelectAllLoaded } =
    useAssetsTableContext();
  const state = computeGroupCheckboxState({
    itemIds: loadedAssetIds,
    isItemSelected: (id) => selectedAssetIds.has(id),
  });

  return (
    <SelectionCheckboxFrame>
      <Checkbox
        size="sm"
        aria-label={t('selectAllVisible')}
        checked={toCheckboxChecked(state)}
        disabled={loadedAssetIds.length === 0}
        onCheckedChange={(details) =>
          onSelectAllLoaded(details.checked === true)
        }
      />
    </SelectionCheckboxFrame>
  );
}

export function AssetsTableSelectionCell({
  row,
}: {
  row: AssetsTableRow | undefined;
}) {
  const t = useTranslations('proxy.assets.table');
  const {
    selectedAssetIds,
    collapsedGroupKeys,
    onSetAssetIdsSelected,
    onToggleGroupCollapsed,
  } = useAssetsTableContext();

  if (!row) {
    return null;
  }

  if (isAssetsTableGroupHeaderRow(row)) {
    return (
      <SelectionCheckboxFrame>
        <GroupToggleButton
          groupKey={row.groupKey}
          isCollapsed={collapsedGroupKeys.has(row.groupKey)}
          onToggle={onToggleGroupCollapsed}
        />
      </SelectionCheckboxFrame>
    );
  }

  return (
    <SelectionCheckboxFrame>
      <Checkbox
        size="sm"
        aria-label={t('selectAsset')}
        checked={selectedAssetIds.has(row.id)}
        onCheckedChange={(details) =>
          onSetAssetIdsSelected({
            assetIds: [row.id],
            selected: details.checked === true,
          })
        }
      />
    </SelectionCheckboxFrame>
  );
}
